import { TIME_LABEL_GAP_PX } from "../constants";
import { TIME_LEVELS, majorLevelIndex } from "./timeLevels";
import { LABEL_SAMPLES, DEFAULT_LABEL_SAMPLE } from "./timeFormat";

/**
 * Finest level whose labels do not crowd: levels shorter than a bar are
 * skipped, every bar starts a period of the bar-sized level anyway.
 */
const chooseLevel = ({ barDurationMs, barSpacing, measureLabel }) => {
  const longerIndex = TIME_LEVELS.findIndex((level) => level.durationMs > barDurationMs);
  const shortestIndex = longerIndex === -1 ? TIME_LEVELS.length - 1 : Math.max(0, longerIndex - 1);

  for (let index = shortestIndex; index < TIME_LEVELS.length; index += 1) {
    const level = TIME_LEVELS[index];
    const sample = LABEL_SAMPLES[level.unit] || DEFAULT_LABEL_SAMPLE;
    const spacing = (Math.max(level.durationMs, barDurationMs) / barDurationMs) * barSpacing;

    if (spacing >= measureLabel(sample, false) + TIME_LABEL_GAP_PX) return index;
  }

  return TIME_LEVELS.length - 1;
};

const overlaps = (tick, placed) =>
  placed.some(
    (other) =>
      tick.left < other.right + TIME_LABEL_GAP_PX && other.left < tick.right + TIME_LABEL_GAP_PX,
  );

/**
 * Time-axis labels for the visible bars. Bars that start a period of the
 * chosen level become candidates; coarser periods win when labels would
 * overlap, so labels never collide at any width (E16).
 *
 * @param {object} params
 * @param {Int8Array} params.weights      - from computeTimeWeights
 * @param {{ first: number, last: number }} params.range - visible bars
 * @param {object} params.timeScale
 * @param {number} params.barDurationMs
 * @param {(text: string, isBold: boolean) => number} params.measureLabel
 * @param {(index: number, unit: string) => string} params.formatLabel
 * @returns {Array<{ x: number, text: string, isBold: boolean }>} sorted by x
 */
export const buildTimeTicks = ({
  weights,
  range,
  timeScale,
  barDurationMs,
  measureLabel,
  formatLabel,
}) => {
  const levelIndex = chooseLevel({ barDurationMs, barSpacing: timeScale.barSpacing, measureLabel });
  const boldFrom = majorLevelIndex(levelIndex);
  const candidates = [];

  for (let index = range.first; index <= range.last; index += 1) {
    const weight = weights[index];

    if (weight >= levelIndex) {
      const isBold = weight >= boldFrom;
      const text = formatLabel(index, TIME_LEVELS[weight].unit);
      const x = timeScale.indexToX(index);
      const halfWidth = measureLabel(text, isBold) / 2;

      candidates.push({ x, text, isBold, weight, left: x - halfWidth, right: x + halfWidth });
    }
  }

  candidates.sort((first, second) => second.weight - first.weight || first.x - second.x);

  const placed = [];

  candidates.forEach((tick) => {
    const isInside = tick.left >= 0 && tick.right <= timeScale.width;

    if (isInside && !overlaps(tick, placed)) placed.push(tick);
  });

  return placed.sort((first, second) => first.x - second.x);
};
